import { CommonModule } from "@angular/common";
import { Component, Input, effect, inject } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTabsModule } from "@angular/material/tabs";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { subscribeInComponent } from "src/app/shared/angular-utils";

import {
    $state,
} from "app/ajs-upgraded-providers";

import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";

import {
    EntityManagerService
} from "src/app/core";

import {
    BookingShifts,
    Bookings,
    BookingsTables,
    Customers,
    Rooms,
    RoomsTables,
    SalesCustomer
} from "tilby-models";

import {
    BookingsDataInput,
    BookingsStateService
} from "src/app/features/bookings";

import {
    AddEditNewBookingDialogService
} from "src/app/dialogs/bookings/add-edit-new-booking-dialog/add-edit-new-booking-dialog.component";

import {
    AlertDialogService
} from "src/app/dialogs";

import {
    MatSnackBar
} from "@angular/material/snack-bar";

import {
    ActiveSaleService,
    SaleTransactionUtilsService
} from "src/app/features/cashregister";

import {
    keyBy
} from "src/app/shared/utils";

type TablesById = {
    table_name: string,
    table_id: number,
    room_name: string,
    room_id?: number,
    covers: number,
    type: string
};

@Component({
    selector: "app-booking-data",
    templateUrl: "./booking-data.component.html",
    styleUrls: ["./booking-data.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        MatTabsModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatDividerModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        TilbyDatePipe,
        TilbyGesturesDirective,
        NgxSkeletonLoaderModule
    ],
})
export class BookingDataComponent {
    private readonly state = inject($state);
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly _snackBar = inject(MatSnackBar);
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly entityManager = inject(EntityManagerService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly bookingsStateService = inject(BookingsStateService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly saleTransactionUtils = inject(SaleTransactionUtilsService);
    private readonly addEditBookingDialogService = inject(AddEditNewBookingDialogService);

    isTablet: boolean = navigator.userAgent.match(/(iPhone|iPad|Android|Mobile)/i) !== null;

    tabIndex = 0;
    tabLabels: string[] = [
        this.translateService.instant("BOOKINGS.NEW_BOOKING_LIST.ALL"),
        this.translateService.instant("BOOKINGS.NEW_BOOKING_LIST.CONFIRMED"),
        this.translateService.instant("BOOKINGS.NEW_BOOKING_LIST.PROVISIONAL")
    ];

    @Input() rooms: Rooms[] = [];
    @Input() bookings: Bookings[] = [];

    bookingsDataInput!: BookingsDataInput;
    tablesById: Record<string, TablesById> = {};
    bookingShifts: BookingShifts[] = [];

    dayBookings: Bookings[] = [];

    bookingsAll: Bookings[] = [];
    bookingsConfirmed: Bookings[] = [];
    bookingsProvisional: Bookings[] = [];

    shift: BookingShifts | undefined = undefined;
    shiftSelected: BookingShifts | undefined = undefined;

    bookingCustomers: Customers[] = [];
    bookingCustomersFiltered: Customers[] = [];

    filter = "";
    filteredCustomer: Customers[] = [];

    bookingStatuses = ['provisional', 'confirmed', 'arrived', 'seated', 'departed', 'noshow'].map(status => ({
        id: status,
        name: this.translateService.instant(`BOOKINGS.STATUSES.${status.toUpperCase()}`) as string
    }));

    bookingStatusesMap: Record<string, string> = this.bookingStatuses.reduce((acc, status) => {
        acc[status.id] = status.name;
        return acc;
    }, {} as Record<string, string>);

    openFilter = false;
    loadingData = false;
    tableAndRoom: { table: any, room: any } = { table: null, room: null };

    isLanded = true;

    constructor() {
        subscribeInComponent(this.bookingsStateService.bookingObservable, (updatedBookings) => {
            this.bookings = updatedBookings;
            this.bookingsDataInput = this.bookingsStateService.selectBookingsData();
            this.onFilterChange();
        });

        effect(() => {
            this.filter = "";
            this.openFilter = false;
            this.bookingsDataInput = this.bookingsStateService.selectBookingsData();
            this.onFilterChange();
        }, { allowSignalWrites: true });

        effect(() => {
            if (this.bookingsStateService.isRefreshData()) {
                this.bookings = this.bookingsStateService.bookingData();
                this.onFilterChange();
                this.bookingsStateService.isRefreshData.set(false);
            }
        }, { allowSignalWrites: true });

        effect(() => {
            if (this.bookingsStateService.bookingData()) {
                this.bookings = this.bookingsStateService.bookingData();
                this.bookingsDataInput = this.bookingsStateService.selectBookingsData();
                this.onFilterChange();
            }
        }, { allowSignalWrites: true });

        subscribeInComponent(this.bookingsStateService.tableEventEmitter, ({ table, room }) => {
            // If the table and room are the same, close the filter
            if (this.tableAndRoom.table && this.tableAndRoom.room && this.tableAndRoom.table.id === table.id && this.tableAndRoom.room.id === room.id) {
                this.openFilter = false;
                this.filter = "";
                this.tableAndRoom = { table: null, room: null };
                this.onFilterChange();
                return;
            }

            // filter the bookings
            if (!this.isLanded) {
                this.filter = `${table.name} ${room.name}`;
                this.tableAndRoom = { table, room };
                this.onFilterChange();
                this.openFilter = true;
            }

            // If there are no bookings, add a new one
            if (!this.isLanded && !this.bookingsAll.length && !this.bookingsConfirmed.length && !this.bookingsProvisional.length) {
                const options = {
                    tableId: table.id
                }
                this.addNewBooking(options);
                this.clearFilter();
                this.openFilter = false;
            }

            this.isLanded = false;
        });
    }

    // the ngOnInit function is used to get the data, populate tablesById and bookingStatuses
    ngOnInit() {
        this.getData();

        for (const room of this.rooms) {
            for (const table of room.tables || []) {
                if (!table.id || table.name.startsWith('ph_')) {
                    continue;
                }

                this.tablesById[table.id] = {
                    table_name: table.name,
                    table_id: table.id,
                    room_name: room.name,
                    room_id: room.id,
                    covers: table.covers,
                    type: table.order_type
                };
            }
        }
    }

    // this function is used to get the data (shifts, bookings, bookingShifts)
    private async getData() {
        this.loadingData = true;
        this.bookings = this.bookingsStateService.bookingData();
        this.bookingShifts = await this.entityManagerService.bookingShifts.fetchCollectionOffline();
        this.bookingsDataInput = this.bookingsStateService.selectBookingsData();
        this.loadingData = false;

        this.filterDayBookings();
    }

    // this function is used to add a new booking - open the dialog
    protected addNewBooking(options?: { tableId?: any }) {
        this.addEditBookingDialogService.openDialog({ data: { bookings: this.bookings, bookingShifts: this.bookingShifts, rooms: this.rooms, options } })
            .then((result) => {
                if (result) {
                    const resultDate = this.tilbyDatePipe.transform(TilbyDatePipe.date(result.booking.booked_for), 'YYYY-MM-dd');
                    const bookingDate = TilbyDatePipe.date({ outputFormat: 'date', date: resultDate || '' });

                    this.bookingsStateService.addBooking(result.booking, bookingDate);
                }
            });
    }

    // this function is used to open and close the filter
    protected filterOpen() {
        this.openFilter = !this.openFilter;
    }

    // this function is used to filter the bookings by date and customer for shift
    private filterDayBookings() {
        this.bookingsAll = this.dayBookings = this.bookings.filter((booking) => {
            const bookedForDate = new Date(booking.booked_for);

            return (
                bookedForDate.getDate() === this.bookingsDataInput.date.getDate() &&
                bookedForDate.getMonth() === this.bookingsDataInput.date.getMonth() &&
                bookedForDate.getFullYear() === this.bookingsDataInput.date.getFullYear()
            );
        }).sort((a, b) => new Date(a.booked_for).getTime() - new Date(b.booked_for).getTime());

        this.updateBookingsStatusViews();
        this.filterCustomerForShift(this.dayBookings);
    }

    private updateBookingsStatusViews() {
        this.bookingsConfirmed = this.bookingsAll.filter((booking) => ["confirmed", "arrived", "seated", "departed"].includes(booking.status))
        this.bookingsProvisional = this.bookingsAll.filter((booking) => booking.status === "provisional");

        this.setBookingsFiltered();
    }

    // this function is used when the shift is changed by the user in the UI
    protected onShiftChange(event?: { value?: BookingShifts, source?: any }) {
        this.shiftSelected = event?.value;

        if (!this.shiftSelected) {
            this.bookingsAll = this.filter.trim()
                ? this.dayBookings.filter((booking) => booking.customer_id == this.filteredCustomer[0].id)
                : this.dayBookings;
        } else {
            const shiftStartTime = new Date(this.bookingsDataInput.date);
            const [startHours, startMinutes, startSeconds] = this.shiftSelected.start_time.split(":").map(x => parseInt(x));

            shiftStartTime.setHours(startHours, startMinutes, startSeconds);


            const shiftEndTime = new Date(this.bookingsDataInput.date);
            const [endHours, endMinutes, endSeconds] = this.shiftSelected.end_time.split(":").map(x => parseInt(x));

            shiftEndTime.setHours(endHours, endMinutes, endSeconds);

            if (this.shiftSelected.end_time < this.shiftSelected.start_time) {
                shiftEndTime.setDate(shiftEndTime.getDate() + 1);
            }

            this.bookingsAll = this.dayBookings.filter((booking) => {
                const bookingTime = new Date(booking.booked_for);

                if (bookingTime < shiftStartTime || bookingTime > shiftEndTime) {
                    return false;
                }

                if (this.filter.trim() && !this.filteredCustomer.some(customer => customer.id == booking.booking_customer?.id)) {
                    return false;
                }

                return true;
            });
        }

        this.updateBookingsStatusViews();
    }

    // this function is used to filter customer for shift
    private filterCustomerForShift(bookings: Bookings[]) {
        const customers: Record<number, Customers> = {};

        for (const booking of bookings) {
            if (booking.booking_customer?.id) {
                customers[booking.booking_customer.id] = booking.booking_customer;
            }
        }

        return Object.values(customers);
    }

    // this function is used to change the booking's list when user change the tab between confirmed, provisional and all
    protected onTabChange(event: any) {
        this.tabIndex = event.index;

        this.setBookingsFiltered();
    }

    // this function is used to filter bookings when user insert something in the filter input like table, room, customer, pms_reservation_name and notes
    protected onFilterChange() {
        this.filterDayBookings();

        const filterString = this.filter.toLowerCase();

        if (!filterString.trim()) {
            this.onShiftChange({ value: this.shiftSelected });
        } else {
            const tables = Object.values(this.tablesById);
            const filteredRooms = this.rooms.filter(room => room.name?.toLowerCase().includes(filterString));

            let filteredTables = (this.tableAndRoom.table && this.tableAndRoom.room)
                ? tables.filter(t => this.tableAndRoom.room.id === t.room_id && t.table_name?.toLowerCase() === this.tableAndRoom.table.name.toLowerCase())
                : [];

            if (!filteredTables.length) {
                filteredTables = tables.filter(table => table.table_name?.toLowerCase().includes(filterString));
            }

            if (filteredTables.length) {
                //Filter by table (from UI map / text search)
                const filteredTablesById = keyBy(filteredTables, t => t.table_id);

                this.bookingsAll = this.dayBookings.filter((booking) => booking.tables?.some(table => !!filteredTablesById[table.table_id!]));
            } else if (filteredRooms.length) {
                //Filter by room (from text search)
                this.bookingsAll = this.dayBookings.filter((booking) => booking.tables?.some((table) => table.table_id && this.tablesById[table.table_id]?.room_name.toLowerCase().includes(filterString)));
            } else {
                //Filter by customer (from text search)
                this.filteredCustomer = this.bookingCustomersFiltered.filter(customer =>
                    customer.first_name?.toLowerCase().includes(filterString) ||
                    customer.last_name?.toLowerCase().includes(filterString) ||
                    `${customer.first_name?.toLowerCase()} ${customer.last_name?.toLowerCase()}`.includes(filterString)
                );

                this.bookingsAll = this.dayBookings.filter((booking) =>
                    booking.booking_customer?.first_name?.toLowerCase().includes(filterString) ||
                    booking.booking_customer?.last_name?.toLowerCase().includes(filterString) ||
                    `${booking.booking_customer?.first_name?.toLowerCase()} ${booking.booking_customer?.last_name?.toLowerCase()}`.includes(filterString) ||
                    booking.pms_reservation_name && booking.pms_reservation_name.toLowerCase().includes(filterString) ||
                    booking.notes && booking.notes.toLowerCase().includes(filterString)
                );
            }

            this.updateBookingsStatusViews();
        }
    }

    // this function is used to clear the filter
    protected clearFilter() {
        this.tableAndRoom = { table: null, room: null };
        this.filter = "";
        this.onFilterChange();
    }

    // this function is used to edit a booking - open the dialog
    protected async editBooking(edit_booking: Bookings, event?: any) {
        const booking = { ...edit_booking };

        const result = await this.addEditBookingDialogService.openDialog({ data: { bookings: this.bookings, bookingShifts: this.bookingShifts, rooms: this.rooms, options: { booking } } });

        if (!result) {
            return;
        }

        if (result.action == 'delete') {
            this.bookingsStateService.deleteBooking(edit_booking);
        } else if (result.booking) {
            this.bookingsStateService.editBooking(result.booking);
        }

        this.bookingsStateService.isRefreshData.set(true);
    }

    protected getStatusClass(status: string): string {
        switch (status) {
            case 'confirmed': return 'status-confirmed';
            case 'provisional': return 'status-provisional';
            case 'arrived': return 'status-arrived';
            case 'cancelled': return 'status-cancelled';
            default: return '';
        }
    }

    private async updateBookingStatus(event: any, booking: Bookings, status: Bookings.StatusEnum) {
        booking.status = status;

        const savedBooking = await this.entityManager.bookings.putOneOnline(booking);

        const index = this.bookings.findIndex(booking => booking.id === savedBooking.id);
        this.bookings.splice(index, 1, savedBooking);

        this.filterDayBookings();
    }

    // this function is used to edit the status of a booking on a rush and change his background color
    protected async editBookingStatus(event: any, booking: Bookings) {
        const newStatus = event.value;

        if (newStatus !== 'arrived' || booking.tables?.length === 0 || !booking.booking_customer?.id) {
            await this.updateBookingStatus(event, booking, event.value);
            return;
        }

        const customer = await this.entityManager.customers.fetchOneOffline(booking.booking_customer?.id);
        const sales = await this.saleTransactionUtils.fetchOpenSales();

        const targetTable = booking.tables?.find((value: BookingsTables) => {
            return value.table_id !== undefined && (this.tablesById[Number(value.table_id)].type === 'multiple' || !sales.some((sale) => sale.table_id === Number(value.table_id)));
        });

        if (!targetTable) {
            this.alertDialogService.openDialog({ data: { messageLabel: 'BOOKINGS.NEW_BOOKING_DIALOG.NO_FREE_TABLE' } });
            return;
        }

        await this.updateBookingStatus(event, booking, event.value);

        //@ts-expect-error 
        const saleCustomer: SalesCustomer = {
            ...(customer || {}),
            customer_id: customer && customer.id,
            id: undefined
        }

        const tableId = Number(targetTable?.table_id);
        const tableInfo = (tableId && this.tablesById[tableId]) || { table_id: undefined, table_name: undefined, room_id: undefined, room_name: undefined };
        const saleExists = sales.filter(sale => sale.booking_id === booking.id);

        if (saleExists.length === 0) {
            const savedSale = await this.activeSaleService.createNewSale(undefined, {
                dontOpenSale: true,
                overrides: {
                    name: this.translateService.instant('TABLES_NEW.DIALOGS.SALE.NAME_TEMPLATE'),
                    order_type: "normal",
                    table_id: tableInfo.table_id,
                    table_name: tableInfo.table_name,
                    room_id: tableInfo.room_id,
                    room_name: tableInfo.room_name,
                    covers: booking.people,
                    booking_id: booking.id,
                    booking_external_id: booking.external_id,
                    sale_customer: saleCustomer
                }
            });

            const snackBarRef = this._snackBar.open(this.translateService.instant('BOOKINGS.NEW_BOOKING_DIALOG.ORDER_CREATED'), 'Ok', {
                horizontalPosition: 'start',
                verticalPosition: 'bottom',
                duration: 3000,
            });

            snackBarRef.onAction().subscribe(() => {
                this.state.go('app.new.cashregister.content.showcase', {
                    action: 'open-sale-id',
                    id: savedSale.id
                });
            });
        } else {
            const snackBarRef = this._snackBar.open(this.translateService.instant('BOOKINGS.NEW_BOOKING_DIALOG.ORDER_CREATED'), 'Ok', {
                horizontalPosition: 'start',
                verticalPosition: 'bottom',
                duration: 3000,
            });

            snackBarRef.onAction().subscribe(() => {
                this.state.go('app.new.cashregister.content.showcase', {
                    action: 'open-sale-id',
                    id: saleExists[0].id
                });
            });
        }
    }

    // this function is useful to go to next day
    protected swipeRight(event: any) {
        this.bookingsDataInput.date.setDate(this.bookingsDataInput.date.getDate() + (this.bookingsDataInput.type !== 0 ? +1 : +7));
        this.bookingsStateService.selectBookingsData.set({
            date: this.bookingsDataInput.date,
            type: this.bookingsDataInput.type
        });
    }

    // this function is useful to go to previous day
    protected swipeLeft(event: any) {
        this.bookingsDataInput.date.setDate(this.bookingsDataInput.date.getDate() + (this.bookingsDataInput.type !== 0 ? -1 : -7));
        this.bookingsStateService.selectBookingsData.set({
            date: this.bookingsDataInput.date,
            type: this.bookingsDataInput.type
        });
    }

    protected trackByFn(index: number, tabLabel: string): number {
        return index;
    }

    // this function is really important because it's the function that filters the list
    private setBookingsFiltered() {
        if (this.tabIndex == 0) {
            this.bookingsStateService.bookingFiltered.set(this.bookingsAll);
        } else if (this.tabIndex == 1) {
            this.bookingsStateService.bookingFiltered.set(this.bookingsConfirmed);
        } else if (this.tabIndex == 2) {
            this.bookingsStateService.bookingFiltered.set(this.bookingsProvisional);
        }

        if (this.shiftSelected) this.bookingsStateService.shiftFiltered.set(this.shiftSelected);
    }

    // Method triggered when an element is dragged over the target
    protected onDragOver(event: DragEvent) {
        event.preventDefault(); // Prevent default to allow drop
    }

    // this function is used to edit a booking by drag and drop of a table on a booking of the list
    protected async onDrop(event: DragEvent, booking: Bookings & { _bookedFor?: any, _customerName?: any }) {
        event.preventDefault(); // Prevent default action

        const data = event.dataTransfer?.getData('application/json'); // Get the JSON string

        if (!data) {
            return;
        }

        const targetTable: RoomsTables = JSON.parse(data); // Parse the JSON string into an object
        const table = this.tablesById[targetTable.id!];

        if (!table) {
            return;
        }

        const tables = new Set(booking.tables?.map((table) => parseInt(table.table_id!)));

        if (tables.has(table.table_id)) {
            return;
        }

        tables.add(table.table_id);

        Object.assign(booking, {
            tables: [...tables].map((tableId) => ({ table_id: tableId })).sort((a, b) => (a.table_id! > b.table_id! ? 1 : -1)) || [],
            booked_for: booking.booked_for,
            external_id: ''
        });

        const { _bookedFor, booking_customer, _customerName, ...bookingToSave } = booking;

        await this.entityManager.bookings.putOneOnline(bookingToSave);

        this.bookingsStateService.editBooking(booking);
    }
}